import { useNormalizedActivityStore } from '../stores/useNormalizedActivityStore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react';
import { msg, plural } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { useAuthStore, usePresenceStore, useTypingStore } from '../stores';
import { useChannelMembers } from '../hooks/useChannelMembers';
import {
  captureAgentActivitySnapshotBaseline,
  countBackgroundRuns,
  useAgentActivityStore,
  type AgentPanelState,
} from '../stores/useAgentActivityStore';
import { fetchChannelAgentActivities } from '../lib/api';
import { useDismissibleLayer } from '../hooks/useDismiss';
import {
  agentLineSignature,
  normalizedAgentLineSignature,
  collapsedSegments,
  mergeActivePeople,
  parseAgentLines,
  parseTypists,
  typistSignature,
  type ActivePerson,
} from '../lib/channel-activity';
import ActivityPanel from './ActivityPanel';
import { dismissBackgroundRun, readBackgroundRunDismissals } from '../lib/backgroundRunDismissals';
import { useGuildId } from '../hooks/useGuildId';
import StopTurnButton, { type StopOutcome } from './StopTurnButton';

/**
 * Who is doing something in this channel right now, above the composer.
 *
 * One line per person, where a person is a human or an agent, deduplicated by
 * who rather than by which rail spoke — the same agent produces a typing
 * keepalive and activity reports for one turn, and those are one fact.
 *
 * The bar is always here and always the same height, so nothing it says or stops
 * saying moves the composer or the message list under the reader. It is a line of
 * text and nothing more: the words are the control, and there is no icon or
 * ground to say so.
 *
 * This whole surface subscribes here and nowhere else. Reading either store from
 * the channel view would re-render the message list at report frequency, and the
 * message list re-parses every loaded message's markdown when it does.
 */
export default function ActivityBar({ channelId }: { channelId: string }) {
  const { i18n } = useLingui();
  const typists = useTypists(channelId);
  const agentLines = useAgentLines(channelId);
  const roster = useChannelAgentNames(channelId);
  const serverId = useGuildId();
  const userId = useAuthStore(s => s.currentUser?.id ?? '');
  const backgroundRunState = useAgentActivityStore(s => backgroundRunSignature(s.byChannel[channelId]));
  const [dismissedRunIds, setDismissedRunIds] = useState<Set<string>>(() =>
    readBackgroundRunDismissals(serverId, userId));

  useAgentActivitySnapshot(channelId);

  useEffect(() => {
    setDismissedRunIds(readBackgroundRunDismissals(serverId, userId));
  }, [serverId, userId]);

  const people = useMemo(
    () => mergeActivePeople(typists, agentLines, roster),
    [typists, agentLines, roster],
  );
  const segments = useMemo(() => collapsedSegments(people), [people]);
  const agents = useMemo(() => people.filter(person => person.agent), [people]);
  const backgroundRuns = useMemo(() => {
    const activityByAgent = useAgentActivityStore.getState().byChannel[channelId];
    return Object.entries(activityByAgent ?? {}).flatMap(([agentId, agent]) =>
      agent.backgroundRuns
        .filter(run => !dismissedRunIds.has(run.runId))
        .map(run => {
          const parent = agent.activities.find(activity => activity.id === run.parentActivityId);
          return {
            ...run,
            agentId,
            agentName: roster.get(agentId) ?? agentId,
            parentActivityLabel: parent ? (parent.description || parent.label) : undefined,
          };
        }));
  }, [channelId, backgroundRunState, dismissedRunIds, roster]);
  const backgroundRunCounts = useMemo(() => countBackgroundRuns(backgroundRuns), [backgroundRuns]);
  const dismissRun = useCallback((runId: string) => {
    setDismissedRunIds(dismissBackgroundRun(serverId, userId, runId));
  }, [serverId, userId]);

  const outcomes = useStopOutcomes(channelId, agents);

  // Stopping a turn is offered in exactly one place. One agent and the bar
  // carries it, because the line already names it and the button cannot mean
  // anything else; more than one and the bar cannot say which it would stop, so
  // each block in the panel carries its own. Nothing moves when the panel opens.
  const soleAgent = agents.length === 1 ? agents[0] : null;

  const panel = usePanel(agents.some(agent => agent.detailed) || agents.length > 1 || backgroundRuns.length > 0);
  const panelId = `activity-panel-${channelId}`;

  return (
    <div className="activity-bar" ref={panel.rootRef}>
      {panel.expanded && (
        <ActivityPanel
          channelId={channelId}
          panelId={panelId}
          panelRef={panel.panelRef}
          agents={agents}
          outcomes={outcomes.byAgent}
          onOutcome={outcomes.record}
          onClose={panel.close}
          stopInBlocks={soleAgent === null}
          backgroundRuns={backgroundRuns}
          onDismissBackgroundRun={dismissRun}
        />
      )}
      <button
        type="button"
        className="activity-bar-line"
        ref={panel.lineRef}
        aria-expanded={panel.expanded}
        aria-controls={panel.expanded ? panelId : undefined}
        aria-disabled={!panel.expandable}
        tabIndex={panel.expandable ? 0 : -1}
        onMouseDown={panel.rememberOrigin}
        onClick={panel.toggle}
      >
        {(people.length > 0 || backgroundRunCounts.active > 0) && <ActivityDots />}
        {segments.map(segment => (
          <span key={segment.status} className="activity-bar-segment" data-status={segment.status}>
            {i18n._(segment.text)}
          </span>
        ))}
        {backgroundRunCounts.total > 0 && (
          <span
            className="activity-bar-segment"
            data-status={backgroundRunCounts.active > 0 ? 'working' : 'idle'}
          >
            {i18n._(backgroundRunCountText(backgroundRunCounts))}
          </span>
        )}
      </button>
      {soleAgent && (
        <StopTurnButton
          className="activity-stop-btn"
          channelId={channelId}
          agentId={soleAgent.id}
          displayName={soleAgent.name}
          outcome={outcomes.byAgent.get(soleAgent.id) ?? 'idle'}
          onOutcome={outcomes.record}
        />
      )}
    </div>
  );
}

function backgroundRunSignature(agents: Record<string, AgentPanelState> | undefined): string {
  return JSON.stringify(Object.fromEntries(
    Object.entries(agents ?? {}).map(([agentId, agent]) => [
      agentId,
      agent.backgroundRuns.map(run => ({
        runId: run.runId,
        label: run.label,
        state: run.state,
        parentActivityId: run.parentActivityId,
        parentActivityLabel: (() => {
          const parent = agent.activities.find(activity => activity.id === run.parentActivityId);
          return parent ? (parent.description || parent.label) : undefined;
        })(),
        startedAt: run.startedAt,
        finishedAt: run.finishedAt,
        reason: run.reason,
        stopSupported: run.stopSupported,
        stopUnsupportedReason: run.stopUnsupportedReason,
      })),
    ]),
  ));
}

function backgroundRunCountText({ active, total }: { active: number; total: number }): MessageDescriptor {
  if (active === total) return msg`${plural(total, { one: '# background run', other: '# background runs' })}`;
  return msg`${plural(active, { one: '# active', other: '# active' })} · ${plural(total, { one: '# background run', other: '# background runs' })}`;
}

function ActivityDots() {
  return (
    <span className="activity-dots" aria-hidden="true">
      <span className="activity-dot" />
      <span className="activity-dot" />
      <span className="activity-dot" />
    </span>
  );
}

/**
 * Who is typing, as ids and names.
 *
 * A keepalive lands every two seconds for as long as someone types and writes a
 * fresh expiry each time, so subscribing to the map itself would re-render this
 * bar at that rate for a line whose words did not change.
 */
function useTypists(channelId: string) {
  const signature = useTypingStore(s => typistSignature(s.typingUsers[channelId]));
  return useMemo(() => parseTypists(signature), [signature]);
}

/**
 * Which agents are working or waiting, and nothing about what they are doing.
 *
 * Output changes several times a second; who is on the rail and which line they
 * are on changes when something actually happened.
 */
function useAgentLines(channelId: string) {
  const signature = useAgentActivityStore(s => agentLineSignature(s.byChannel[channelId]));
  const normalizedSignature = useNormalizedActivityStore(s => normalizedAgentLineSignature(s.byChannel[channelId]));
  return useMemo(() => {
    const lines = new Map(parseAgentLines(signature).map(line => [line.id, line]));
    for (const line of parseAgentLines(normalizedSignature)) {
      const existing = lines.get(line.id);
      lines.set(line.id, { ...line, status: existing?.status === 'waiting' ? 'waiting' : line.status, detailed: line.detailed || existing?.detailed === true });
    }
    return [...lines.values()];
  }, [signature, normalizedSignature]);
}

/**
 * The bar's open/closed state, and where the reader's focus came from.
 *
 * The panel is a look-at-it-now surface with nothing to persist, so it is
 * dismissed by leaving it: a press anywhere outside — the composer included,
 * since starting to type means you have stopped looking — Escape, its own close
 * button, or the bar again.
 */
function usePanel(expandable: boolean) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const originRef = useRef<HTMLElement | null>(null);

  /**
   * Gives focus back to where the reader was, but only when the panel still
   * holds it — a pointer outside decides for itself where focus lands, most
   * often the composer at the caret that was clicked, and must not have it
   * pulled back out from under it.
   *
   * The bar's own line is the fallback because it is always mounted: the panel
   * is what goes away, and leaving focus on a removed element drops it to the
   * document body with no way back.
   */
  const close = useCallback(() => {
    const origin = originRef.current;
    const held = panelRef.current?.contains(document.activeElement);
    originRef.current = null;
    setExpanded(false);
    if (held) (origin ?? lineRef.current)?.focus();
  }, []);

  const rememberOrigin = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    originRef.current = active && panelRef.current?.contains(active) ? null : active;
  }, []);

  const toggle = useCallback(() => {
    if (!expandable) return;
    if (expanded) {
      close();
      return;
    }
    // Keyboard activation fires no mousedown, and there the reader is already on
    // the bar, which is exactly where Escape should return them.
    if (!originRef.current) originRef.current = document.activeElement as HTMLElement | null;
    setExpanded(true);
  }, [expandable, expanded, close]);

  useEffect(() => {
    if (expanded) panelRef.current?.focus();
  }, [expanded]);

  // Nothing left to open means nothing left to look at. The next turn opens
  // collapsed rather than resuming whatever a reader last left open about work
  // that has since ended.
  useEffect(() => {
    if (!expandable) setExpanded(false);
  }, [expandable]);

  // Escape and a press outside go through the app's layer stack rather than a
  // listener of this bar's own, so a dialog opened over the panel takes the key
  // first and a press the dialog caught is not a press outside the panel.
  //
  // A press decides for itself where focus lands, and the caret it puts in the
  // composer is the point of dismissing this way, so nothing is handed back.
  useDismissibleLayer({
    active: expanded,
    within: rootRef,
    onEscape: close,
    onOutsidePress: () => {
      originRef.current = null;
      setExpanded(false);
    },
    // The click that opens the panel bubbles after this render. If the panel
    // joins the global outside-press stack immediately, that opening gesture can
    // be re-read as an outside press against the freshly mounted layer. Deferring
    // registration one frame keeps the opening click from dismissing the panel,
    // while every later outside press still closes it.
    deferred: true,
  });

  return { expanded, expandable, rootRef, lineRef, panelRef, toggle, close, rememberOrigin };
}

/**
 * Each agent's stop outcome, held here rather than inside its button, because a
 * button is unmounted every time the panel closes and the answer to a click —
 * including one still in flight — has to outlive that.
 */
function useStopOutcomes(channelId: string, agents: ActivePerson[]) {
  const [byAgent, setByAgent] = useState<ReadonlyMap<string, StopOutcome>>(new Map());

  const record = useCallback(
    (agentId: string, outcome: StopOutcome) =>
      setByAgent(prev => new Map(prev).set(agentId, outcome)),
    [],
  );

  // An agent that has gone quiet keeps no answer, so its next turn starts from
  // "Stop" rather than from the previous turn's result.
  useEffect(() => {
    const active = new Set(agents.map(agent => agent.id));
    setByAgent(prev => {
      const kept = new Map([...prev].filter(([agentId]) => active.has(agentId)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [agents]);

  // A switch changes which channel these answers were about, so none survives.
  useEffect(() => {
    setByAgent(prev => (prev.size === 0 ? prev : new Map()));
  }, [channelId]);

  return { byAgent, record };
}

/**
 * The channel's agents by display name, and by the fact that they are agents.
 *
 * The activity rail carries an agent id and nothing else — it is stamped from
 * the reporting connection rather than taken from the report — so the roster is
 * what turns it into a name. Typing carries a name but no role, so the roster is
 * also what says a typist may be offered a Stop button; a roster that could not
 * be read leaves every typist unstoppable, which is the honest degradation for
 * an affordance that must never offer to stop a person.
 */
function useChannelAgentNames(channelId: string) {
  const { members } = useChannelMembers(channelId);

  return useMemo(
    () => new Map(members.filter(m => m.role === 'agent').map(m => [m.user_id, m.display_name])),
    [members],
  );
}

/**
 * Reads what the channel's agents were already doing before this reader was
 * listening, and again whenever the socket has been away.
 *
 * Without it the bar reads empty and says so confidently until the next report
 * happens to arrive, which for an agent inside a long tool call can be minutes.
 * Re-reading on reconnect is the same cold start: the reports that crossed the
 * gap are gone and nothing replays them.
 *
 * Typing has no such read and needs none: it is purely ephemeral, and a typist
 * who is still typing re-announces themselves within their own heartbeat.
 */
function useAgentActivitySnapshot(channelId: string): void {
  const connectionState = usePresenceStore(s => s.connectionState);

  useEffect(() => {
    if (connectionState !== 'connected') return;
    let cancelled = false;

    // Taken before the request goes out, so the answer can be told apart from
    // anything that arrives while it is in flight. The socket is subscribed to
    // every channel long before one is opened, so that is routine.
    const askedWith = captureAgentActivitySnapshotBaseline(
      useAgentActivityStore.getState().byChannel[channelId],
    );

    fetchChannelAgentActivities(channelId)
      .then(({ agents }) => {
        if (cancelled) return;
        useAgentActivityStore.getState().applySnapshot(channelId, agents, askedWith);
      })
      .catch(err => {
        console.warn('[agent-activity] snapshot failed for %s:', channelId, err);
      });

    return () => {
      cancelled = true;
    };
  }, [channelId, connectionState]);
}
